import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getCertificates, STUDENT_QUERY_OPTIONS, STUDENT_QUERY_KEYS } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent } from "@/components/ui/card";
import { Award } from "lucide-react";
import { CertificateCard } from "@/components/certificates/CertificateCard";
import { CardSkeleton } from "@/components/loading";

export const Route = createFileRoute("/_authenticated/certificates")({
  component: CertificatesPage,
});

function CertificatesPage() {
  const { user } = useAuth();

  const { data: certificates = [], isLoading } = useQuery({
    queryKey: STUDENT_QUERY_KEYS.userCertificates(user?.id),
    queryFn: () => getCertificates(user!.id),
    enabled: !!user?.id,
    ...STUDENT_QUERY_OPTIONS,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Award className="h-6 w-6 text-primary" />
          My Certificates
        </h1>
        <p className="text-sm text-muted-foreground">
          View your earned certificates and share verification links with employers.
        </p>
      </div>

      {isLoading ? (
        <CardSkeleton count={3} className="md:grid-cols-2 lg:grid-cols-3" />
      ) : certificates.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-20 text-center">
            <Award className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="font-semibold text-lg">No certificates yet</h3>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto mt-2">
              Complete courses or learning tracks to earn official certifications.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {certificates.map((cert) => (
            <CertificateCard key={cert.id} certificate={cert} />
          ))}
        </div>
      )}
    </div>
  );
}
